/** A period, set in the API, in which a sample of sessions records replays continuously. */
export interface ReplayCaptureWindow {
  /** Share of sessions (0–1) that record continuously while the window is open. */
  sampleRate: number;
  endsAt: Date;
}

const SAMPLE_KEY = 'tio_replay_window_sample';

/**
 * Reads the `telemetry-client-configs` document from `GET /api/public/telemetry/config`.
 *
 * @returns the open window, or null when none is set, it has already ended, or the document
 *   is not the expected shape (including a sample rate outside 0–1)
 */
export function parseReplayCaptureWindow(
  document: unknown,
  now = new Date()
): ReplayCaptureWindow | null {
  const attributes = (document as { data?: { attributes?: Record<string, unknown> } } | null)?.data
    ?.attributes;
  if (attributes?.['replay-mode'] !== 'continuous') return null;

  const sampleRate = attributes['replay-sample-rate'];
  const endsAt = new Date(String(attributes['replay-ends-at']));
  const validRate =
    typeof sampleRate === 'number' &&
    Number.isFinite(sampleRate) &&
    sampleRate > 0 &&
    sampleRate <= 1;
  if (!validRate || Number.isNaN(endsAt.getTime()) || endsAt <= now) return null;
  return { sampleRate, endsAt };
}

/**
 * Whether this browser session is one of the window's sample. Drawn once per session and
 * window: reloads keep the answer, and a new or re-timed window draws again. Reads and
 * writes `sessionStorage`; when it is unavailable the draw is simply not remembered.
 */
export function sampledIntoWindow(
  window: ReplayCaptureWindow,
  storage: Pick<Storage, 'getItem' | 'setItem'> | undefined = globalThis.sessionStorage,
  random: () => number = Math.random
) {
  const key = window.endsAt.toISOString();
  try {
    const stored = JSON.parse(storage?.getItem(SAMPLE_KEY) ?? 'null') as {
      window?: string;
      sampled?: boolean;
    } | null;
    if (stored?.window === key && typeof stored.sampled === 'boolean') return stored.sampled;
  } catch {
    // An unreadable or blocked store falls through to a fresh draw.
  }

  const sampled = random() < window.sampleRate;
  try {
    storage?.setItem(SAMPLE_KEY, JSON.stringify({ window: key, sampled }));
  } catch {
    // Private windows can refuse writes; the draw then lasts only this page load.
  }
  return sampled;
}
